import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI, trainingSessionAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
  LocationDropdown,
  TrainingTypeDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';
import styled from 'styled-components';

const ListSection = styled.section`
  background: var(--bg-primary);
  border-radius: var(--radius-md);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid var(--card-border);
  box-shadow: var(--shadow-sm);
`;

const ListTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: left;
`;


const AthleteTabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid var(--card-border);
  flex-wrap: wrap;
`;

const AthleteTab = styled.button<{ $isActive: boolean }>`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: ${props => props.$isActive ? '3px solid var(--accent-orange)' : '3px solid transparent'};
  color: ${props => props.$isActive ? 'var(--accent-orange)' : 'var(--text-gray)'};
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: ${props => props.$isActive ? 900 : 600};
  text-transform: capitalize;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: var(--accent-orange);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const ListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    white-space: nowrap;
  }
`;

const ListTableHeader = styled.th`
  text-align: left;
  padding: 0.75rem;
  color: var(--text-gray);
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: var(--mobile-list-header, 0.6rem);
    padding: 0.5rem 0.3rem;
  }
`;

const ListTableCell = styled.td`
  padding: 0.75rem;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    font-size: var(--mobile-list-cell, 0.65rem);
    padding: 0.5rem 0.3rem;
  }
`;

const ListTableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

// Custom bar shape with rounded top corners
const RoundedBar = (props: any) => {
  const { fill, x, y, width, height } = props;
  const radius = 8;
  const path = `
    M ${x},${y + height}
    L ${x},${y + radius}
    Q ${x},${y} ${x + radius},${y}
    L ${x + width - radius},${y}
    Q ${x + width},${y} ${x + width},${y + radius}
    L ${x + width},${y + height}
    Z
  `;
  
  return (
    <g>
      <path
        d={path}
        fill={fill}
        style={{
          filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.15))',
        }}
      />
    </g>
  );
};


interface GroundTraining {
  athleteId: string | { _id: string; firstName: string; lastName: string };
  trainingType: string;
  intensity: number;
}

interface TrainingSession {
  _id: string;
  date: string;
  location: string;
  trainingType: string;
  groundTrainings?: GroundTraining[];
}

interface GroundTrainingAnalyticsProps {
  isJumperMode?: boolean;
}

const GroundTrainingAnalytics: React.FC<GroundTrainingAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedLocation, setSelectedLocation] = useState<string>(savedFilters?.selectedLocation || '');
  const [selectedTrainingTypes, setSelectedTrainingTypes] = useState<string[]>(savedFilters?.selectedTrainingTypes || []);
  const [selectedIntensityPeriod, setSelectedIntensityPeriod] = useState<'daily' | 'weekly' | 'monthly'>(savedFilters?.selectedPeriod || 'daily');
  const [selectedAthleteForTrainingTypes, setSelectedAthleteForTrainingTypes] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= 768);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Data states for graphs
  const [intensityLineDataDaily, setIntensityLineDataDaily] = useState<any[]>([]);
  const [intensityLineDataWeekly, setIntensityLineDataWeekly] = useState<any[]>([]);
  const [intensityLineDataMonthly, setIntensityLineDataMonthly] = useState<any[]>([]);
  const [sessionsPerWeekData, setSessionsPerWeekData] = useState<any[]>([]);
  const [sessionsPerMonthData, setSessionsPerMonthData] = useState<any[]>([]);
  const [sessionsPerAthleteBarData, setSessionsPerAthleteBarData] = useState<any[]>([]);
  const [sessionsByTypeList, setSessionsByTypeList] = useState<Array<{ type: string; count: number }>>([]);
  const [avgIntensityByTypeList, setAvgIntensityByTypeList] = useState<Array<{ type: string; avgIntensity: number }>>([]);
  const [sessionsByTypePerAthlete, setSessionsByTypePerAthlete] = useState<Map<string, Array<{ type: string; count: number }>>>(new Map());
  const [avgIntensityByTypePerAthlete, setAvgIntensityByTypePerAthlete] = useState<Map<string, Array<{ type: string; avgIntensity: number }>>>(new Map());

  const trainingTypeLabels: Record<string, string> = {
    'coordination': 'Coordination',
    'power': 'Power',
    'speed': 'Speed',
    'stretching': 'Stretching',
    'specialized_coordination': 'Specialized Coordination',
    'imitation_ski_jump': 'Imitation Ski Jump',
    'run': 'Run',
    'visualisation': 'Visualisation',
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, sessionsResponse] = await Promise.all([
          athleteAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        setAthletes(athletesData);
        
        // Filter only ground training sessions
        const groundTrainingSessions = Array.isArray(sessionsData) ? sessionsData.filter((s: TrainingSession) => 
          s.trainingType === 'ground_training' && 
          s.groundTrainings && 
          s.groundTrainings.length > 0
        ) : [];
        setSessions(groundTrainingSessions);

        // Extract unique locations
        const uniqueLocations = Array.from(new Set(
          groundTrainingSessions.map((s: TrainingSession) => s.location).filter(Boolean)
        ));
        setLocations(uniqueLocations as string[]);

        // Set default date range (last 3 months) only if no saved filters exist
        if (!savedFilters || (!savedFilters.startDate && !savedFilters.endDate)) {
          const today = new Date();
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(today.getMonth() - 3);
          setEndDate(today.toISOString().split('T')[0]);
          setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      startDate,
      endDate,
      selectedLocation,
      selectedTrainingTypes,
      selectedPeriod: selectedIntensityPeriod,
    });
  }, [pagePath, selectedAthletes, startDate, endDate, selectedLocation, selectedTrainingTypes, selectedIntensityPeriod]);

  // Helper function to format date for input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to check if date is in range
  const isDateInRange = (dateString: string, start: string, end: string): boolean => {
    if (!start || !end) return true;
    return dateString >= start && dateString <= end;
  };

  // Process ground training data
  useEffect(() => {
    if (!sessions.length || !startDate || !endDate || selectedAthletes.length === 0) {
      setIntensityLineDataDaily([]);
      setIntensityLineDataWeekly([]);
      setIntensityLineDataMonthly([]);
      setSessionsPerWeekData([]);
      setSessionsPerMonthData([]);
      setSessionsPerAthleteBarData([]);
      setSessionsByTypeList([]);
      setAvgIntensityByTypeList([]);
      return;
    }

    // Filter sessions
    const filteredSessions = sessions.filter(session => {
      if (!isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedLocation && session.location !== selectedLocation) return false;
      return true;
    });

    // Get athletes to process
    const athletesToProcess = athletes.filter(a => selectedAthletes.includes(a._id));

    // Collect intensity data per athlete and date
    const athleteIntensityMap = new Map<string, Map<string, number[]>>(); // athleteId -> date -> intensities[]
    
    // Collect session counts per week/month per athlete
    const weekSessionMap = new Map<string, Map<string, number>>(); // weekKey -> athleteId -> count
    const monthSessionMap = new Map<string, Map<string, number>>(); // monthKey -> athleteId -> count
    
    // Collect session counts per athlete
    const athleteSessionCountMap = new Map<string, number>(); // athleteId -> count
    
    // Collect data by training type per athlete
    const typeSessionCountMapPerAthlete = new Map<string, Map<string, number>>(); // athleteId -> type -> count
    const typeIntensityMapPerAthlete = new Map<string, Map<string, number[]>>(); // athleteId -> type -> intensities[]
    
    // Collect data by training type (overall for all selected athletes)
    const typeSessionCountMap = new Map<string, number>(); // type -> count
    const typeIntensityMap = new Map<string, number[]>(); // type -> intensities[]

    filteredSessions.forEach(session => {
      if (!session.groundTrainings) return;

      const sessionDate = new Date(session.date);
      const weekStart = new Date(sessionDate);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Start of week (Monday)
      const weekKey = weekStart.toISOString().split('T')[0];
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      session.groundTrainings.forEach(training => {
        const athleteIdRaw = typeof training.athleteId === 'object' ? training.athleteId._id : training.athleteId;
        const athleteId = String(athleteIdRaw);
        
        if (!selectedAthletes.includes(athleteId)) return;
        if (selectedTrainingTypes.length > 0 && !selectedTrainingTypes.includes(training.trainingType)) return;

        // Add to intensity map
        if (!athleteIntensityMap.has(athleteId)) {
          athleteIntensityMap.set(athleteId, new Map());
        }
        const dateMap = athleteIntensityMap.get(athleteId)!;
        if (!dateMap.has(session.date)) {
          dateMap.set(session.date, []);
        }
        dateMap.get(session.date)!.push(training.intensity);

        // Count sessions per athlete
        athleteSessionCountMap.set(athleteId, (athleteSessionCountMap.get(athleteId) || 0) + 1);

        // Count sessions per week per athlete
        if (!weekSessionMap.has(weekKey)) {
          weekSessionMap.set(weekKey, new Map());
        }
        const weekAthleteMap = weekSessionMap.get(weekKey)!;
        weekAthleteMap.set(athleteId, (weekAthleteMap.get(athleteId) || 0) + 1);

        // Count sessions per month per athlete
        const monthKey = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}`;
        if (!monthSessionMap.has(monthKey)) {
          monthSessionMap.set(monthKey, new Map());
        }
        const monthAthleteMap = monthSessionMap.get(monthKey)!;
        monthAthleteMap.set(athleteId, (monthAthleteMap.get(athleteId) || 0) + 1);

        // Count by training type per athlete
        if (!typeSessionCountMapPerAthlete.has(athleteId)) {
          typeSessionCountMapPerAthlete.set(athleteId, new Map());
        }
        const athleteTypeCountMap = typeSessionCountMapPerAthlete.get(athleteId)!;
        athleteTypeCountMap.set(training.trainingType, (athleteTypeCountMap.get(training.trainingType) || 0) + 1);

        if (!typeIntensityMapPerAthlete.has(athleteId)) {
          typeIntensityMapPerAthlete.set(athleteId, new Map());
        }
        const athleteTypeIntensityMap = typeIntensityMapPerAthlete.get(athleteId)!;
        if (!athleteTypeIntensityMap.has(training.trainingType)) {
          athleteTypeIntensityMap.set(training.trainingType, []);
        }
        athleteTypeIntensityMap.get(training.trainingType)!.push(training.intensity);

        // Count by training type (overall for all selected athletes)
        typeSessionCountMap.set(training.trainingType, (typeSessionCountMap.get(training.trainingType) || 0) + 1);
        if (!typeIntensityMap.has(training.trainingType)) {
          typeIntensityMap.set(training.trainingType, []);
        }
        typeIntensityMap.get(training.trainingType)!.push(training.intensity);
      });
    });

    // Generate date range for intensity line chart
    const start = new Date(startDate);
    const end = new Date(endDate);
    const allDates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      allDates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    // Process intensity line data - Daily
    const intensityLineChartDataDaily: any[] = [];
    allDates.forEach(date => {
      const point: any = { date };
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteIntensityMap.get(athleteIdStr);
        
        if (dateMap && dateMap.has(date)) {
          const intensities = dateMap.get(date)!;
          const avg = intensities.reduce((a, b) => a + b, 0) / intensities.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      intensityLineChartDataDaily.push(point);
    });

    // Process intensity line data - Weekly
    const weekStartDateForIntensity = new Date(start);
    weekStartDateForIntensity.setDate(weekStartDateForIntensity.getDate() - weekStartDateForIntensity.getDay());
    const intensityLineChartDataWeekly: any[] = [];
    const checkWeekForIntensity = new Date(weekStartDateForIntensity);
    while (checkWeekForIntensity <= end) {
      const weekEnd = new Date(checkWeekForIntensity);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = checkWeekForIntensity.toISOString().split('T')[0];
      const weekLabel = checkWeekForIntensity.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const point: any = { week: weekKey, weekLabel };
      
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteIntensityMap.get(athleteIdStr);
        
        const weekIntensities: number[] = [];
        const checkDate = new Date(checkWeekForIntensity);
        while (checkDate <= weekEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            weekIntensities.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (weekIntensities.length > 0) {
          const avg = weekIntensities.reduce((a, b) => a + b, 0) / weekIntensities.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      
      intensityLineChartDataWeekly.push(point);
      checkWeekForIntensity.setDate(checkWeekForIntensity.getDate() + 7);
    }

    // Process intensity line data - Monthly
    const monthStartDateForIntensity = new Date(start.getFullYear(), start.getMonth(), 1);
    const intensityLineChartDataMonthly: any[] = [];
    const checkMonthForIntensity = new Date(monthStartDateForIntensity);
    while (checkMonthForIntensity <= end) {
      const monthEnd = new Date(checkMonthForIntensity.getFullYear(), checkMonthForIntensity.getMonth() + 1, 0);
      const monthKey = `${checkMonthForIntensity.getFullYear()}-${String(checkMonthForIntensity.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = checkMonthForIntensity.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const point: any = { month: monthLabel };
      
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteIntensityMap.get(athleteIdStr);
        
        const monthIntensities: number[] = [];
        const checkDate = new Date(checkMonthForIntensity);
        while (checkDate <= monthEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            monthIntensities.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (monthIntensities.length > 0) {
          const avg = monthIntensities.reduce((a, b) => a + b, 0) / monthIntensities.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      
      intensityLineChartDataMonthly.push(point);
      checkMonthForIntensity.setMonth(checkMonthForIntensity.getMonth() + 1);
    }

    // Process sessions per week (with athlete lines and date range)
    const weekStartDateForSessions = new Date(start);
    weekStartDateForSessions.setDate(weekStartDateForSessions.getDate() - weekStartDateForSessions.getDay() + 1); // Start of week (Monday)
    const sessionsPerWeek: any[] = [];
    const checkWeekForSessions = new Date(weekStartDateForSessions);
    while (checkWeekForSessions <= end) {
      const weekKey = checkWeekForSessions.toISOString().split('T')[0];
      const weekEndDate = new Date(checkWeekForSessions);
      weekEndDate.setDate(weekEndDate.getDate() + 6);
      const weekLabel = checkWeekForSessions.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const weekEndLabel = weekEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const weekData: any = {
        week: weekLabel,
        weekKey,
        weekStart: checkWeekForSessions.toISOString().split('T')[0],
        weekEnd: weekEndDate.toISOString().split('T')[0],
        weekRange: `${weekLabel} - ${weekEndLabel}`,
      };
      
      // Add counts for each athlete
      const weekAthleteMap = weekSessionMap.get(weekKey);
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        weekData[athleteName] = weekAthleteMap?.get(athleteIdStr) || 0;
      });
      
      sessionsPerWeek.push(weekData);
      checkWeekForSessions.setDate(checkWeekForSessions.getDate() + 7);
    }

    // Process sessions per month (with athlete lines)
    const monthStartDateForSessions = new Date(start.getFullYear(), start.getMonth(), 1);
    const sessionsPerMonth: any[] = [];
    const checkMonthForSessions = new Date(monthStartDateForSessions);
    while (checkMonthForSessions <= end) {
      const monthKey = `${checkMonthForSessions.getFullYear()}-${String(checkMonthForSessions.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = checkMonthForSessions.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const monthData: any = { month: monthLabel };
      
      // Add counts for each athlete
      const monthAthleteMap = monthSessionMap.get(monthKey);
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        monthData[athleteName] = monthAthleteMap?.get(athleteIdStr) || 0;
      });
      
      sessionsPerMonth.push(monthData);
      checkMonthForSessions.setMonth(checkMonthForSessions.getMonth() + 1);
    }

    // Process sessions per athlete bar chart
    const sessionsPerAthlete: any[] = [];
    athletesToProcess.forEach(athlete => {
      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      const athleteIdStr = String(athlete._id);
      sessionsPerAthlete.push({
        athlete: athleteName,
        athleteId: athleteIdStr,
        sessions: athleteSessionCountMap.get(athleteIdStr) || 0,
      });
    });
    sessionsPerAthlete.sort((a, b) => b.sessions - a.sessions);

    // Process lists
    const sessionsByType: Array<{ type: string; count: number }> = [];
    typeSessionCountMap.forEach((count, type) => {
      sessionsByType.push({
        type: trainingTypeLabels[type] || type,
        count,
      });
    });
    sessionsByType.sort((a, b) => b.count - a.count);

    const avgIntensityByType: Array<{ type: string; avgIntensity: number }> = [];
    typeIntensityMap.forEach((intensities, type) => {
      const avg = intensities.reduce((a, b) => a + b, 0) / intensities.length;
      avgIntensityByType.push({
        type: trainingTypeLabels[type] || type,
        avgIntensity: Math.round(avg * 10) / 10,
      });
    });
    avgIntensityByType.sort((a, b) => b.avgIntensity - a.avgIntensity);

    // Process data per athlete for training types
    const sessionsByTypePerAthleteMap = new Map<string, Array<{ type: string; count: number }>>();
    const avgIntensityByTypePerAthleteMap = new Map<string, Array<{ type: string; avgIntensity: number }>>();

    athletesToProcess.forEach(athlete => {
      const athleteIdStr = String(athlete._id);
      const athleteTypeCountMap = typeSessionCountMapPerAthlete.get(athleteIdStr);
      const athleteTypeIntensityMap = typeIntensityMapPerAthlete.get(athleteIdStr);

      if (athleteTypeCountMap) {
        const sessionsByType: Array<{ type: string; count: number }> = [];
        athleteTypeCountMap.forEach((count, type) => {
          sessionsByType.push({
            type: trainingTypeLabels[type] || type,
            count,
          });
        });
        sessionsByType.sort((a, b) => b.count - a.count);
        sessionsByTypePerAthleteMap.set(athleteIdStr, sessionsByType);
      }

      if (athleteTypeIntensityMap) {
        const avgIntensityByType: Array<{ type: string; avgIntensity: number }> = [];
        athleteTypeIntensityMap.forEach((intensities, type) => {
          const avg = intensities.reduce((a, b) => a + b, 0) / intensities.length;
          avgIntensityByType.push({
            type: trainingTypeLabels[type] || type,
            avgIntensity: Math.round(avg * 10) / 10,
          });
        });
        avgIntensityByType.sort((a, b) => b.avgIntensity - a.avgIntensity);
        avgIntensityByTypePerAthleteMap.set(athleteIdStr, avgIntensityByType);
      }
    });

    // Set default selected athlete for training types if not set and we have athletes
    // Also reset if currently selected athlete is no longer in selected athletes
    if (selectedAthletes.length > 0) {
      if (!selectedAthleteForTrainingTypes || !selectedAthletes.includes(selectedAthleteForTrainingTypes)) {
        if (athletesToProcess.length > 0) {
          setSelectedAthleteForTrainingTypes(athletesToProcess[0]._id);
        }
      }
    } else {
      // Clear selection if no athletes selected
      setSelectedAthleteForTrainingTypes('');
    }

    setIntensityLineDataDaily(intensityLineChartDataDaily);
    setIntensityLineDataWeekly(intensityLineChartDataWeekly);
    setIntensityLineDataMonthly(intensityLineChartDataMonthly);
    setSessionsPerWeekData(sessionsPerWeek);
    setSessionsPerMonthData(sessionsPerMonth);
    setSessionsPerAthleteBarData(sessionsPerAthlete);
    setSessionsByTypeList(sessionsByType);
    setAvgIntensityByTypeList(avgIntensityByType);
    setSessionsByTypePerAthlete(sessionsByTypePerAthleteMap);
    setAvgIntensityByTypePerAthlete(avgIntensityByTypePerAthleteMap);
  }, [sessions, startDate, endDate, selectedAthletes, selectedLocation, selectedTrainingTypes, athletes]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleUpdateDate = () => {
    // Data will be re-processed automatically via useEffect
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleTrainingTypeToggle = (type: string) => {
    setSelectedTrainingTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  const handleTrainingTypesSave = (types: string[]) => {
    setSelectedTrainingTypes(types);
  };


  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading ground training data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Ground Training Analytics</PageTitle>
        <PageDescription>
          Analyze ground training sessions, including training types, intensity levels, and their correlation with performance.
        </PageDescription>
      </PageHeader>

      {/* Data Selection Section */}
      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}

          <SelectionGroup>
            <LocationDropdown
              locations={locations}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
            />
          </SelectionGroup>

          <SelectionGroup>
            <TrainingTypeDropdown
              selectedTrainingTypes={selectedTrainingTypes}
              onTrainingTypeToggle={handleTrainingTypeToggle}
              onSave={handleTrainingTypesSave}
            />
          </SelectionGroup>
        </SelectionGrid>
      </DataSelectionSection>

      {/* Graphs Section */}
      
      {/* Intensity Line Chart with Period Selector */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>{isJumperMode ? 'Intensity' : 'Intensity for Each Athlete'}</GraphTitle>
        {(() => {
          const hasDailyData = intensityLineDataDaily.length > 0 && intensityLineDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasWeeklyData = intensityLineDataWeekly.length > 0 && intensityLineDataWeekly.some(d => Object.keys(d).some(k => k !== 'week' && d[k] !== null));
          const hasMonthlyData = intensityLineDataMonthly.length > 0 && intensityLineDataMonthly.some(d => Object.keys(d).some(k => k !== 'month' && d[k] !== null));
          const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
          
          return hasAnyData ? (
            <PeriodSelectorTopRight>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedIntensityPeriod === 'daily'}
                  onClick={() => setSelectedIntensityPeriod('daily')}
                  title="Daily"
                >
                  D
                </PeriodButtonSmall>
                <PeriodLabelSmall>Daily</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedIntensityPeriod === 'weekly'}
                  onClick={() => setSelectedIntensityPeriod('weekly')}
                  title="Weekly"
                >
                  W
                </PeriodButtonSmall>
                <PeriodLabelSmall>Weekly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedIntensityPeriod === 'monthly'}
                  onClick={() => setSelectedIntensityPeriod('monthly')}
                  title="Monthly"
                >
                  M
                </PeriodButtonSmall>
                <PeriodLabelSmall>Monthly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
            </PeriodSelectorTopRight>
          ) : null;
        })()}
        {startDate && endDate && selectedAthletes.length > 0 ? (
          (() => {
            const currentData = selectedIntensityPeriod === 'daily' ? intensityLineDataDaily : selectedIntensityPeriod === 'weekly' ? intensityLineDataWeekly : intensityLineDataMonthly;
            const dataKey = selectedIntensityPeriod === 'daily' ? 'date' : selectedIntensityPeriod === 'weekly' ? 'week' : 'month';
            const hasData = currentData.length > 0 && currentData.some(d => Object.keys(d).some(k => k !== dataKey && d[k] !== null));
            
            return hasData ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart data={currentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={dataKey} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        if (selectedIntensityPeriod === 'monthly') return value;
                        if (selectedIntensityPeriod === 'weekly') {
                          try {
                            const date = new Date(value);
                            if (isNaN(date.getTime())) return value;
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          } catch {
                            return value;
                          }
                        }
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis 
                      domain={[0, 10]}
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                    {athletes
                      .filter(a => selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            connectNulls={true}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </GraphContainer>
            ) : (
              <EmptyState>
                No intensity data available for the selected period and filters
              </EmptyState>
            );
          })()
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Sessions Per Week Line Chart */}
      <GraphSection>
        <GraphTitle>Number of Ground Training Sessions per Week</GraphTitle>
        {startDate && endDate && selectedAthletes.length > 0 && sessionsPerWeekData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <LineChart data={sessionsPerWeekData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <YAxis 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip 
                  formatter={(value: any, name: string) => [value, name]}
                  labelFormatter={(label, payload) => {
                    if (payload && payload.length > 0 && payload[0].payload.weekRange) {
                      return payload[0].payload.weekRange;
                    }
                    return label;
                  }}
                />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {athletes
                  .filter(a => selectedAthletes.includes(a._id))
                  .map((athlete, index) => {
                    const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                    return (
                      <Line
                        key={athlete._id}
                        type="monotone"
                        dataKey={athleteName}
                        stroke={getAthleteColor(athlete, index)}
                        strokeWidth={2}
                        name={athleteName}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSection>

      {/* Sessions Per Month Line Chart */}
      <GraphSection>
        <GraphTitle>Number of Ground Training Sessions per Month</GraphTitle>
        {startDate && endDate && selectedAthletes.length > 0 && sessionsPerMonthData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <LineChart data={sessionsPerMonthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <YAxis 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {athletes
                  .filter(a => selectedAthletes.includes(a._id))
                  .map((athlete, index) => {
                    const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                    return (
                      <Line
                        key={athlete._id}
                        type="monotone"
                        dataKey={athleteName}
                        stroke={getAthleteColor(athlete, index)}
                        strokeWidth={2}
                        name={athleteName}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSection>

      {/* Sessions Per Athlete Bar Chart - Hidden in jumper mode */}
      {!isJumperMode && (
      <GraphSection>
        <GraphTitle>Number of Ground Training Sessions</GraphTitle>
        {startDate && endDate && selectedAthletes.length > 0 && sessionsPerAthleteBarData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <BarChart data={sessionsPerAthleteBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="athlete" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <YAxis 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                <Bar 
                  dataKey="sessions" 
                  shape={<RoundedBar />}
                >
                  {sessionsPerAthleteBarData.map((entry, index) => {
                    const athlete = athletes.find(a => a._id === entry.athleteId);
                    const color = athlete ? getAthleteColor(athlete, index) : '#00cc66';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSection>
      )}

      {/* Lists Section */}
      
      {/* Sessions By Type */}
      <ListSection>
        <ListTitle>Number of Sessions for Each Training Type</ListTitle>
        {selectedAthletes.length > 0 && sessionsByTypePerAthlete.size > 0 ? (
          <>
            <AthleteTabsContainer>
              {athletes
                .filter(a => selectedAthletes.includes(a._id))
                .map(athlete => (
                  <AthleteTab
                    key={athlete._id}
                    $isActive={selectedAthleteForTrainingTypes === athlete._id}
                    onClick={() => setSelectedAthleteForTrainingTypes(athlete._id)}
                  >
                    {athlete.firstName} {athlete.lastName}
                  </AthleteTab>
                ))}
            </AthleteTabsContainer>
            {selectedAthleteForTrainingTypes && sessionsByTypePerAthlete.has(selectedAthleteForTrainingTypes) ? (
              (() => {
                const athleteData = sessionsByTypePerAthlete.get(selectedAthleteForTrainingTypes)!;
                return athleteData.length > 0 ? (
                  <ListTable>
                    <thead>
                      <tr>
                        <ListTableHeader>Training Type</ListTableHeader>
                        <ListTableHeader>Number of Sessions</ListTableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {athleteData.map((item, index) => (
                        <ListTableRow key={index}>
                          <ListTableCell>{item.type}</ListTableCell>
                          <ListTableCell>{item.count}</ListTableCell>
                        </ListTableRow>
                      ))}
                    </tbody>
                  </ListTable>
                ) : (
                  <EmptyState>No training type data available for this athlete</EmptyState>
                );
              })()
            ) : (
              <EmptyState>Please select an athlete</EmptyState>
            )}
          </>
        ) : (
          <EmptyState>No data available</EmptyState>
        )}
      </ListSection>

      {/* Average Intensity By Type */}
      <ListSection>
        <ListTitle>Average Intensity for Each Training Type</ListTitle>
        {selectedAthletes.length > 0 && avgIntensityByTypePerAthlete.size > 0 ? (
          <>
            <AthleteTabsContainer>
              {athletes
                .filter(a => selectedAthletes.includes(a._id))
                .map(athlete => (
                  <AthleteTab
                    key={athlete._id}
                    $isActive={selectedAthleteForTrainingTypes === athlete._id}
                    onClick={() => setSelectedAthleteForTrainingTypes(athlete._id)}
                  >
                    {athlete.firstName} {athlete.lastName}
                  </AthleteTab>
                ))}
            </AthleteTabsContainer>
            {selectedAthleteForTrainingTypes && avgIntensityByTypePerAthlete.has(selectedAthleteForTrainingTypes) ? (
              (() => {
                const athleteData = avgIntensityByTypePerAthlete.get(selectedAthleteForTrainingTypes)!;
                return athleteData.length > 0 ? (
                  <ListTable>
                    <thead>
                      <tr>
                        <ListTableHeader>Training Type</ListTableHeader>
                        <ListTableHeader>Average Intensity</ListTableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {athleteData.map((item, index) => (
                        <ListTableRow key={index}>
                          <ListTableCell>{item.type}</ListTableCell>
                          <ListTableCell>{item.avgIntensity}/10</ListTableCell>
                        </ListTableRow>
                      ))}
                    </tbody>
                  </ListTable>
                ) : (
                  <EmptyState>No training type data available for this athlete</EmptyState>
                );
              })()
            ) : (
              <EmptyState>Please select an athlete</EmptyState>
            )}
          </>
        ) : (
          <EmptyState>No data available</EmptyState>
        )}
      </ListSection>
    </AnalyticsContainer>
  );
};

export default GroundTrainingAnalytics;
